import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const API_BASE_URL = 'https://api.autocasses.com/api';
const MAX_AUCTION_HOURS = 720;
const PLACEHOLDER_IMAGE = 'https://canbind.ca/wp-content/uploads/2025/01/placeholder-image-person-jpg.jpg';

function formatDateMySQL(date = new Date()) {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

const TIME_OPTIONS = [
  { hours: 24, label: '24h', descriptionKey: 'auctionModal.timeOptions.1day' },
  { hours: 48, label: '48h', descriptionKey: 'auctionModal.timeOptions.2days' },
  { hours: 72, label: '72h', descriptionKey: 'auctionModal.timeOptions.3days' },
  { hours: 168, label: '7j', descriptionKey: 'auctionModal.timeOptions.1week' },
  { hours: 336, label: '14j', descriptionKey: 'auctionModal.timeOptions.2weeks' },
  { hours: 720, label: '30j', descriptionKey: 'auctionModal.timeOptions.1month' }
];

const FILTERS = [
  { key: 'all', labelKey: 'filters.all' },
  { key: 'active', labelKey: 'filters.active' },
  { key: 'auction', labelKey: 'filters.auction' },
  { key: 'sold', labelKey: 'filters.sold' }
];

const ProductSkeleton = () => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow animate-pulse">
    <div className="h-48 bg-gray-200"></div>
    <div className="p-4">
      <div className="flex justify-between mb-3">
        <div className="space-y-2 flex-1 mr-3">
          <div className="h-5 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
        <div className="h-6 bg-gray-200 rounded w-1/4"></div>
      </div>
      <div className="h-4 bg-gray-200 rounded w-4/5 mb-3"></div>
      <div className="flex justify-between mb-4">
        <div className="h-4 bg-gray-200 rounded w-2/5"></div>
        <div className="h-4 bg-gray-200 rounded w-2/5"></div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="h-9 bg-gray-200 rounded"></div>
        ))}
      </div>
    </div>
  </div>
);

const StatsCard = ({ number, label }) => (
  <div className="bg-gray-50 px-4 py-3 rounded-xl border border-gray-200 text-center min-w-20">
    <div className="text-2xl font-bold text-blue-500 mb-1">{number}</div>
    <div className="text-xs text-gray-600 font-semibold">{label}</div>
  </div>
);

const AuctionModal = React.memo(({
  visible,
  onClose,
  selectedProduct,
  auctionTime,
  setAuctionTime,
  auctionStartPrice,
  setAuctionStartPrice,
  auctionMinBid,
  setAuctionMinBid,
  isSettingAuction,
  onStartAuction,
  navigate
}) => {
  const { t } = useTranslation();
  const [isMounted, setIsMounted] = useState(false);
  const scrollViewRef = useRef(null);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  const handleClose = useCallback(() => {
    if (!isSettingAuction) {
      onClose();
    }
  }, [isSettingAuction, onClose]);

  const handleStartAuction = useCallback(() => {
    const errors = [];
    
    const price = parseInt(auctionStartPrice);
    if (!auctionStartPrice || isNaN(price) || price <= 0) {
      errors.push(t('auctionModal.validation.startPrice'));
    }
    
    const time = parseInt(auctionTime);
    if (!auctionTime || isNaN(time) || time <= 0) {
      errors.push(t('auctionModal.validation.time'));
    } else if (time > MAX_AUCTION_HOURS) {
      errors.push(t('auctionModal.validation.maxTime', { hours: MAX_AUCTION_HOURS }));
    }
    
    if (auctionMinBid) {
      const minBid = parseInt(auctionMinBid);
      if (isNaN(minBid) || minBid <= 0) {
        errors.push(t('auctionModal.validation.minBid'));
      }
    }
    
    if (errors.length > 0) {
      alert(errors.join('\n'));
      return;
    }
    
    const selectedTimeOption = TIME_OPTIONS.find(opt => opt.hours.toString() === auctionTime);
    const confirmationMessage = t('auctionModal.confirmation.message', {
      duration: selectedTimeOption ? t(selectedTimeOption.descriptionKey) : `${auctionTime} ${t('common.hours')}`,
      price: parseInt(auctionStartPrice).toLocaleString(t('common.currency') === 'FCFA' ? 'fr-FR' : 'en-US'),
      minBid: auctionMinBid ? `${parseInt(auctionMinBid).toLocaleString(t('common.currency') === 'FCFA' ? 'fr-FR' : 'en-US')} ${t('common.currency')}` : ''
    });
    
    if (window.confirm(`${t('auctionModal.confirmation.title')}\n\n${confirmationMessage}`)) {
      onStartAuction();
    }
  }, [auctionTime, auctionStartPrice, auctionMinBid, onStartAuction, t]);

  const formatPrice = useCallback((price) => {
    if (!price) return '0';
    const num = parseInt(price);
    return isNaN(num) ? '0' : num.toLocaleString(t('common.currency') === 'FCFA' ? 'fr-FR' : 'en-US');
  }, [t]);

  if (!visible || !selectedProduct || !isMounted) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 z-50 flex items-end justify-center bg-black bg-opacity-70"
      onClick={handleClose}
    >
      <div 
        className="bg-white rounded-t-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-blue-500 p-6 rounded-t-2xl border-b border-blue-400">
          <div className="flex justify-between items-center">
            <div className="flex items-center flex-1">
              <div className="w-12 h-12 rounded-full bg-blue-400 flex items-center justify-center mr-4">
                <Icon name="Gavel" size={28} color="white" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-white">{t('auctionModal.title')}</h2>
                <p className="text-sm text-blue-100 mt-1">{t('auctionModal.subtitle')}</p>
              </div>
            </div>
            <button 
              className="w-10 h-10 rounded-full bg-blue-400 flex items-center justify-center hover:bg-blue-300 transition-colors"
              onClick={handleClose}
              disabled={isSettingAuction}
            >
              <Icon name="X" size={24} color="white" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Product Section */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-blue-100 rounded-lg mr-3">
                <Icon name="Package" size={20} color="#3b82f6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t('auctionModal.productSection.title')}
              </h3>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 flex items-center">
              <img 
                src={selectedProduct.photos?.[0]?.photo_url || PLACEHOLDER_IMAGE} 
                alt={selectedProduct.titre}
                className="w-20 h-20 rounded-lg object-cover bg-gray-200"
                onError={(e) => {
                  e.target.src = PLACEHOLDER_IMAGE;
                }}
              />
              <div className="ml-4 flex-1">
                <h4 className="font-semibold text-gray-900 line-clamp-2">
                  {selectedProduct.titre || t('common.noTitle')}
                </h4>
                <div className="flex items-center mt-2">
                  <div className="flex items-center bg-purple-50 text-purple-700 px-2 py-1 rounded-md mr-3">
                    <Icon name="Tag" size={14} className="mr-1" />
                    <span className="text-xs font-semibold">{t('common.currentPrice')}</span>
                  </div>
                  <span className="text-xl font-bold text-blue-500">
                    {formatPrice(selectedProduct.prix)} {t('common.currency')}
                  </span>
                </div>
                {selectedProduct.city && (
                  <div className="flex items-center mt-2 text-gray-500">
                    <Icon name="MapPin" size={14} className="mr-1" />
                    <span className="text-sm">{selectedProduct.city}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Settings Section */}
          <div>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-blue-100 rounded-lg mr-3">
                <Icon name="Settings" size={20} color="#3b82f6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t('auctionModal.settingsSection.title')}
              </h3>
            </div>

            {/* Start Price */}
            <div className="bg-white rounded-xl p-5 border border-gray-200 mb-5 shadow-sm">
              <div className="flex items-start mb-5">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center mr-4">
                  <Icon name="DollarSign" size={18} color="#10b981" />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-gray-900 mb-1">
                    {t('auctionModal.settings.startPrice')} *
                  </label>
                  <p className="text-sm text-gray-600">
                    {t('auctionModal.settings.startPriceDescription')}
                  </p>
                </div>
              </div>
              <div className="border-2 border-gray-200 rounded-lg p-3 bg-gray-50 flex items-center mb-2">
                <span className="text-gray-600 font-semibold mr-3">
                  {t('common.currency')}
                </span>
                <input
                  type="text"
                  className="flex-1 text-2xl font-bold text-gray-900 bg-transparent outline-none"
                  value={auctionStartPrice}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^0-9]/g, '');
                    setAuctionStartPrice(cleaned);
                  }}
                  placeholder="0"
                  disabled={isSettingAuction}
                  maxLength={10}
                />
              </div>
              <p className="text-sm text-gray-500 text-center italic">
                {auctionStartPrice 
                  ? `${formatPrice(auctionStartPrice)} ${t('common.currency')}`
                  : t('auctionModal.settings.requiredField')
                }
              </p>
            </div>

            {/* Minimum Bid */}
            <div className="bg-white rounded-xl p-5 border border-gray-200 mb-5 shadow-sm">
              <div className="flex items-start mb-5">
                <div className="w-10 h-10 rounded-lg bg-yellow-50 flex items-center justify-center mr-4">
                  <Icon name="ArrowUp" size={18} color="#f59e0b" />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-gray-900 mb-1">
                    {t('auctionModal.settings.minBid')}
                  </label>
                  <p className="text-sm text-gray-600">
                    {t('auctionModal.settings.minBidDescription')}
                  </p>
                </div>
              </div>
              <div className="border-2 border-gray-200 rounded-lg p-3 bg-gray-50 flex items-center mb-2">
                <span className="text-gray-600 font-semibold mr-3">
                  {t('common.currency')}
                </span>
                <input
                  type="text"
                  className="flex-1 text-2xl font-bold text-gray-900 bg-transparent outline-none"
                  value={auctionMinBid}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^0-9]/g, '');
                    setAuctionMinBid(cleaned);
                  }}
                  placeholder="0"
                  disabled={isSettingAuction}
                  maxLength={10}
                />
              </div>
              <p className="text-sm text-gray-500 text-center italic">
                {auctionMinBid 
                  ? `${formatPrice(auctionMinBid)} ${t('common.currency')}`
                  : t('auctionModal.settings.optional')
                }
              </p>
            </div>

            {/* Duration */}
            <div className="bg-white rounded-xl p-5 border border-gray-200 mb-5 shadow-sm">
              <div className="flex items-start mb-5">
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center mr-4">
                  <Icon name="Clock" size={18} color="#8b5cf6" />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-gray-900 mb-1">
                    {t('auctionModal.settings.duration')}
                  </label>
                  <p className="text-sm text-gray-600">
                    {t('auctionModal.settings.durationDescription')}
                  </p>
                </div>
              </div>

              {/* Time Options Grid */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                {TIME_OPTIONS.map((option) => (
                  <button
                    key={option.hours}
                    className={`
                      flex flex-col items-center p-3 rounded-lg border-2 transition-colors relative
                      ${auctionTime === option.hours.toString() 
                        ? 'bg-blue-500 border-blue-500 text-white' 
                        : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                      }
                    `}
                    onClick={() => !isSettingAuction && setAuctionTime(option.hours.toString())}
                    disabled={isSettingAuction}
                  >
                    <span className={`
                      font-bold text-lg mb-1
                      ${auctionTime === option.hours.toString() ? 'text-white' : 'text-gray-900'}
                    `}>
                      {option.label}
                    </span>
                    <span className={`
                      text-xs
                      ${auctionTime === option.hours.toString() ? 'text-blue-100' : 'text-gray-500'}
                    `}>
                      {t(option.descriptionKey)}
                    </span>
                    {auctionTime === option.hours.toString() && (
                      <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center border-2 border-white">
                        <Icon name="Check" size={12} color="white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {/* Custom Time */}
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-2">
                  {t('auctionModal.settings.customDuration')} :
                </label>
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex-1 border-2 border-gray-200 rounded-lg p-3 bg-gray-50 flex items-center">
                    <input
                      type="text"
                      className="flex-1 text-lg font-semibold text-gray-900 bg-transparent outline-none"
                      value={auctionTime}
                      onChange={(e) => {
                        const cleaned = e.target.value.replace(/[^0-9]/g, '');
                        if (!isSettingAuction && cleaned.length <= 3) {
                          setAuctionTime(cleaned);
                        }
                      }}
                      placeholder={t('auctionModal.settings.customPlaceholder')}
                      disabled={isSettingAuction}
                      maxLength={3}
                    />
                    <span className="ml-2 text-gray-600 font-semibold">
                      {t('common.hours')}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 italic">
                  {t('auctionModal.settings.maxDuration', { hours: MAX_AUCTION_HOURS })}
                </p>
              </div>
            </div>

            {/* Info Card */}
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-200 mb-5">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-blue-100 rounded-lg mr-3">
                  <Icon name="Info" size={20} color="#3b82f6" />
                </div>
                <h4 className="font-semibold text-gray-900">
                  {t('auctionModal.info.title')}
                </h4>
              </div>
              <div className="space-y-3">
                <div className="flex items-start">
                  <Icon name="CheckCircle" size={16} color="#10b981" className="mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    {t('auctionModal.info.point1')}
                  </p>
                </div>
                <div className="flex items-start">
                  <Icon name="CheckCircle" size={16} color="#10b981" className="mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    {t('auctionModal.info.point2')}
                  </p>
                </div>
                <div className="flex items-start">
                  <Icon name="CheckCircle" size={16} color="#10b981" className="mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    {t('auctionModal.info.point3')}
                  </p>
                </div>
                <div className="flex items-start">
                  <Icon name="CheckCircle" size={16} color="#10b981" className="mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    {t('auctionModal.info.point4')}
                  </p>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
              <h4 className="font-semibold text-gray-900 text-center mb-4">
                {t('auctionModal.summary.title')}
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm text-gray-600 font-medium">
                    {t('common.duration')}
                  </span>
                  <span className="font-semibold text-gray-900">
                    {TIME_OPTIONS.find(opt => opt.hours.toString() === auctionTime) 
                      ? t(TIME_OPTIONS.find(opt => opt.hours.toString() === auctionTime).descriptionKey)
                      : `${auctionTime} ${t('common.hours')}`
                    }
                  </span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-sm text-gray-600 font-medium">
                    {t('auctionModal.summary.startPrice')}
                  </span>
                  <span className="font-semibold text-blue-500">
                    {formatPrice(auctionStartPrice)} {t('common.currency')}
                  </span>
                </div>
                {auctionMinBid && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 font-medium">
                      {t('auctionModal.summary.minBid')}
                    </span>
                    <span className="font-semibold text-gray-900">
                      {formatPrice(auctionMinBid)} {t('common.currency')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-white flex gap-3">
          <button
            className={`
              flex-1 flex items-center justify-center py-3 rounded-lg border border-gray-300
              ${isSettingAuction ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}
            `}
            onClick={handleClose}
            disabled={isSettingAuction}
          >
            <Icon name="XCircle" size={18} color="#64748b" className="mr-2" />
            <span className="font-semibold text-gray-600">{t('common.cancel')}</span>
          </button>
          
          <button
            className={`
              flex-[2] flex items-center justify-center py-3 rounded-lg bg-blue-500 text-white
              ${(!auctionStartPrice || isSettingAuction) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'}
            `}
            onClick={handleStartAuction}
            disabled={!auctionStartPrice || isSettingAuction}
          >
            {isSettingAuction ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                <span className="font-semibold">{t('auctionModal.starting')}</span>
              </>
            ) : (
              <>
                <Icon name="Rocket" size={18} color="white" className="mr-2" />
                <span className="font-semibold">{t('auctionModal.startAuction')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
});

const ListHeaderComponent = React.memo(({ 
  stats, 
  searchQuery, 
  handleSearch, 
  activeFilter, 
  handleFilterChange, 
  navigate 
}) => {
  const { t } = useTranslation();
  const searchInputRef = useRef(null);

  return (
    <div className="bg-white pb-2 border-b border-gray-100">
      <div className="container mx-auto px-4 lg:px-6">
        <div className="py-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{t('header.title')}</h1>
            <p className="text-gray-600 mt-1 text-sm md:text-base">{t('header.subtitle')}</p>
          </div>
          <StatsCard number={stats.all} label={t('stats.total')} />
        </div>
        
        <div className="py-3">
          <div className="relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <Icon name="Search" size={20} color="#3b82f6" />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              className="w-full pl-10 pr-10 py-3 bg-gray-50 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              placeholder={t('search.placeholder')}
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && e.target.blur()}
            />
            {searchQuery.length > 0 && (
              <button 
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => handleSearch('')}
              >
                <Icon name="XCircle" size={20} />
              </button>
            )}
          </div>
        </div>

        <div className="py-3 bg-white flex flex-col sm:flex-row items-center gap-4">
          <div className="flex-1 overflow-x-auto scrollbar-hide w-full">
            <div className="flex space-x-2 min-w-max">
              {FILTERS.map((filter) => (
                <button
                  key={filter.key}
                  className={`
                    flex items-center px-4 py-2 rounded-full border-2 transition-colors whitespace-nowrap
                    ${activeFilter === filter.key 
                      ? 'bg-blue-500 border-blue-500 text-white' 
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                    }
                  `}
                  onClick={() => handleFilterChange(filter.key)}
                >
                  <span className={`
                    font-semibold text-sm
                    ${activeFilter === filter.key ? 'text-white' : 'text-gray-600'}
                  `}>
                    {t(filter.labelKey)}
                  </span>
                  <span className={`
                    ml-2 px-2 py-0.5 rounded-full text-xs font-bold
                    ${activeFilter === filter.key 
                      ? 'bg-white text-blue-500' 
                      : 'bg-gray-200 text-gray-600'
                    }
                  `}>
                    {stats[filter.key] || 0}
                  </span>
                </button>
              ))}
            </div>
          </div>
          
          <button 
            className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center hover:bg-blue-600 shadow-lg transition-colors"
            onClick={() => navigate('/create-listing', {
              state: {
                productId: null,
                isEditMode: false,
              }
            })}
          >
            <Icon name="Plus" size={24} color="white" />
          </button>
        </div>
      </div>
    </div>
  );
});

const Produit = React.memo(({ 
  isLoading = false, 
  hasMore = false, 
  onLoadMore,
  currentPage = 1,
  totalPages = 1,
  isOwner = true,
  navigate: propNavigate
}) => {
  const { t } = useTranslation();
  const internalNavigate = useNavigate();
  const navigate = propNavigate || internalNavigate;
  
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [products, setProducts] = useState([]);
  const [internalLoading, setInternalLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [auctionModalVisible, setAuctionModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [auctionTime, setAuctionTime] = useState('24');
  const [auctionStartPrice, setAuctionStartPrice] = useState('');
  const [auctionMinBid, setAuctionMinBid] = useState('');
  const [isSettingAuction, setIsSettingAuction] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    all: 0,
    active: 0,
    auction: 0,
    sold: 0
  });

  const containerRef = useRef(null);

  const fetchProducts = useCallback(async (showLoading = true, silent = false) => {
    if (showLoading && !silent) {
      setInternalLoading(true);
    }
    setError(null);
    
    try {
      const [token, userData] = await Promise.all([
        localStorage.getItem('userToken'),
        localStorage.getItem('userData')
      ]);
      
      if (!token || !userData) {
        setError(t('errors.sessionExpired'));
        setProducts([]);
        setFilteredProducts([]);
        setStats({ all: 0, active: 0, auction: 0, sold: 0 });
        return;
      }
      
      const User = JSON.parse(userData);
      
      const response = await axios.get(
        `${API_BASE_URL}/produit/getproduits/${User.id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          timeout: 15000
        }
      );
      
      const productsData = response.data || [];
      setProducts(productsData);
      setFilteredProducts(productsData);
      
      const newStats = {
        all: productsData.length,
        active: productsData.filter(p => p.inActive === false).length,
        auction: productsData.filter(p => p.enchere === 1).length,
        sold: productsData.filter(p => p.inActive === true).length
      };
      setStats(newStats);
      
    } catch (err) {
      console.error('Erreur lors du chargement des produits:', err);
      
      let errorMessage = t('errors.connection');
      
      if (err.code === 'ECONNABORTED') {
        errorMessage = t('errors.timeout');
      } else if (err.response) {
        switch (err.response.status) {
          case 401:
            errorMessage = t('errors.sessionExpired');
            break;
          case 403:
            errorMessage = t('errors.unauthorized');
            break;
          case 404:
            errorMessage = t('errors.noProducts');
            break;
          case 500:
            errorMessage = t('errors.server');
            break;
          default:
            errorMessage = err.response.data?.message || t('errors.generic');
        }
      }
      
      setError(errorMessage);
      setProducts([]);
      setFilteredProducts([]);
      setStats({ all: 0, active: 0, auction: 0, sold: 0 });
      
    } finally {
      if (showLoading && !silent) {
        setInternalLoading(false);
      }
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    fetchProducts();
    
    return () => {
      setProducts([]);
      setFilteredProducts([]);
    };
  }, [fetchProducts]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    fetchProducts(false, true);
  }, [fetchProducts]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      applyFilter(products, activeFilter);
      return;
    }
    
    const searchTerm = query.toLowerCase().trim();
    const results = products.filter(product => {
      const searchFields = [
        product.titre,
        product.description,
        product.city,
        product.etat,
        product.marque,
        product.modele
      ];
      
      return searchFields.some(field => 
        field?.toLowerCase().includes(searchTerm)
      );
    });
    
    setFilteredProducts(results);
  }, [products, activeFilter]);

  const applyFilter = useCallback((data, filter) => {
    let filtered = data;
    
    switch (filter) {
      case 'active':
        filtered = filtered.filter(product => product.inActive === false);
        break;
      case 'auction':
        filtered = filtered.filter(product => product.enchere === 1);
        break;
      case 'sold':
        filtered = filtered.filter(product => product.inActive === true);
        break;
    }
    
    return filtered;
  }, []);

  const handleFilterChange = useCallback((filter) => {
    setActiveFilter(filter);
    
    let results = products;
    
    results = applyFilter(results, filter);
    
    if (searchQuery.trim()) {
      const searchTerm = searchQuery.toLowerCase().trim();
      results = results.filter(product => {
        const titre = product.titre?.toLowerCase() || '';
        const description = product.description?.toLowerCase() || '';
        return titre.includes(searchTerm) || description.includes(searchTerm);
      });
    }
    
    setFilteredProducts(results);
  }, [products, searchQuery, applyFilter]);

  const handleDelete = useCallback(async (productId) => {
    if (window.confirm(t('delete.confirmMessage'))) {
      try {
        setIsDeleting(true);
        
        const token = localStorage.getItem('userToken');
        
        await axios.delete(
          `${API_BASE_URL}/produit/delete/${productId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            timeout: 10000
          }
        );
        
        const updatedProducts = products.filter(product => product.id !== productId);
        setProducts(updatedProducts);
        
        let updatedFiltered = filteredProducts.filter(product => product.id !== productId);
        
        updatedFiltered = applyFilter(updatedFiltered, activeFilter);
        
        if (searchQuery.trim()) {
          const searchTerm = searchQuery.toLowerCase().trim();
          updatedFiltered = updatedFiltered.filter(product => {
            const titre = product.titre?.toLowerCase() || '';
            const description = product.description?.toLowerCase() || '';
            return titre.includes(searchTerm) || description.includes(searchTerm);
          });
        }
        
        setFilteredProducts(updatedFiltered);
        
        const newStats = {
          all: updatedProducts.length,
          active: updatedProducts.filter(p => p.inActive === false).length,
          auction: updatedProducts.filter(p => p.enchere === 1).length,
          sold: updatedProducts.filter(p => p.inActive === true).length
        };
        setStats(newStats);
        
        alert(t('delete.success'));
        
      } catch (err) {
        console.error('Erreur lors de la suppression:', err);
        
        let errorMessage = t('delete.error');
        
        if (err.response?.status === 404) {
          errorMessage = t('delete.notFound');
        } else if (err.response?.status === 403) {
          errorMessage = t('delete.unauthorized');
        }
        
        alert(errorMessage);
      } finally {
        setIsDeleting(false);
      }
    }
  }, [products, filteredProducts, activeFilter, searchQuery, applyFilter, t]);

  const handleView = useCallback((listing) => {
     navigate('/product', { 
      state: { 
        productId: listing.id,
        productData: listing
      }
    });
  }, [navigate]);

  const openAuctionModal = useCallback((product) => {
    if (!product) return;
    
    setSelectedProduct(product);
    setAuctionTime('24');
    setAuctionStartPrice(product.prix?.toString() || '');
    setAuctionMinBid('');
    setAuctionModalVisible(true);
  }, []);

  const closeAuctionModal = useCallback(() => {
    setAuctionModalVisible(false);
    setTimeout(() => {
      setSelectedProduct(null);
      setAuctionTime('24');
      setAuctionStartPrice('');
      setAuctionMinBid('');
    }, 300);
  }, []);

  const handleStartAuction = useCallback(async () => {
    if (!selectedProduct) return;
    
    const time = parseInt(auctionTime);
    const price = parseInt(auctionStartPrice);
    const minBid = auctionMinBid ? parseInt(auctionMinBid) : null;
    
    if (isNaN(time) || time <= 0 || time > MAX_AUCTION_HOURS) {
      alert(t('auctionModal.validation.invalidTime', { hours: MAX_AUCTION_HOURS }));
      return;
    }
    
    if (isNaN(price) || price <= 0) {
      alert(t('auctionModal.validation.invalidPrice'));
      return;
    }
    
    if (minBid !== null && (isNaN(minBid) || minBid <= 0)) {
      alert(t('auctionModal.validation.invalidMinBid'));
      return;
    }
    
    setIsSettingAuction(true);
    
    try {
      const token = localStorage.getItem('userToken');
      
      const response = await axios.put(
        `${API_BASE_URL}/produit/update/${selectedProduct.id}`,
        {
          enchere: 1,
          temps: time,
          price: price,
          minBid: minBid,
          date_now: formatDateMySQL()
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        }
      );
      
      if (response.data) {
        const updatedProducts = products.map(product => 
          product.id === selectedProduct.id 
            ? { 
                ...product, 
                enchere: 1, 
                temps: time, 
                prix: price,
                minBid: minBid
              }
            : product
        );
        
        setProducts(updatedProducts);
        
        let updatedFiltered = filteredProducts.map(product => 
          product.id === selectedProduct.id 
            ? { 
                ...product, 
                enchere: 1, 
                temps: time, 
                prix: price,
                minBid: minBid
              }
            : product
        );
        
        updatedFiltered = applyFilter(updatedFiltered, activeFilter);
        setFilteredProducts(updatedFiltered);
        
        const newStats = {
          all: updatedProducts.length,
          active: updatedProducts.filter(p => p.inActive === false).length,
          auction: updatedProducts.filter(p => p.enchere === 1).length,
          sold: updatedProducts.filter(p => p.inActive === true).length
        };
        setStats(newStats);
        
        alert(t('auction.success', { time, plural: time > 1 ? 's' : '' }));
        closeAuctionModal();
      }
    } catch (err) {
      console.error('Erreur lors du lancement de l\'enchère:', err);
      
      let errorMessage = t('auction.error');
      
      if (err.response?.status === 400) {
        errorMessage = t('auction.invalidData');
      } else if (err.response?.status === 404) {
        errorMessage = t('auction.productNotFound');
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = t('auction.timeout');
      }
      
      alert(errorMessage);
    } finally {
      setIsSettingAuction(false);
    }
  }, [selectedProduct, auctionTime, auctionStartPrice, auctionMinBid, products, filteredProducts, activeFilter, applyFilter, closeAuctionModal, t]);

  const handleEdit = useCallback((productId) => {
    navigate('/create-listing', {
      state: {
        productId: productId,
        isEditMode: true,
      }
    });
  }, [navigate]);

  const handleStopAuction = useCallback(async (productId) => {
    if (window.confirm(t('auction.stop.message'))) {
      try {
        const token = localStorage.getItem('userToken');
        
        await axios.put(
          `${API_BASE_URL}/produit/update/${productId}`,
          { 
            enchere: 0, 
            temps: 0,
            minBid: null
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        const updatedProducts = products.map(product => 
          product.id === productId 
            ? { 
                ...product, 
                enchere: 0, 
                temps: 0,
                minBid: null
              }
            : product
        );
        
        setProducts(updatedProducts);
        
        let updatedFiltered = filteredProducts.map(product => 
          product.id === productId 
            ? { 
                ...product, 
                enchere: 0, 
                temps: 0,
                minBid: null
              }
            : product
        );
        
        updatedFiltered = applyFilter(updatedFiltered, activeFilter);
        setFilteredProducts(updatedFiltered);
        
        const newStats = {
          all: updatedProducts.length,
          active: updatedProducts.filter(p => p.inActive === false).length,
          auction: updatedProducts.filter(p => p.enchere === 1).length,
          sold: updatedProducts.filter(p => p.inActive === true).length
        };
        setStats(newStats);
        
        alert(t('auction.stop.success'));
        
      } catch (err) {
        console.error('Erreur lors de l\'arrêt de l\'enchère:', err);
        alert(t('auction.stop.error'));
      }
    }
  }, [products, filteredProducts, activeFilter, applyFilter, t]);

  const handleMarkAsSold = useCallback(async (productId) => {
    if (window.confirm(t('markSold.message'))) {
      try {
        const token = localStorage.getItem('userToken');
        
        await axios.put(
          `${API_BASE_URL}/produit/update/${productId}`,
          { inActive: true },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        const updatedProducts = products.map(product => 
          product.id === productId 
            ? { ...product, inActive: true }
            : product
        );
        
        setProducts(updatedProducts);
        
        let updatedFiltered = filteredProducts.map(product => 
          product.id === productId 
            ? { ...product, inActive: true }
            : product
        );
        
        updatedFiltered = applyFilter(updatedFiltered, activeFilter);
        setFilteredProducts(updatedFiltered);
        
        const newStats = {
          all: updatedProducts.length,
          active: updatedProducts.filter(p => p.inActive === false).length,
          auction: updatedProducts.filter(p => p.enchere === 1).length,
          sold: updatedProducts.filter(p => p.inActive === true).length
        };
        setStats(newStats);
        
        alert(t('markSold.success'));
        
      } catch (err) {
        console.error('Erreur:', err);
        alert(t('markSold.error'));
      }
    }
  }, [products, filteredProducts, activeFilter, applyFilter, t]);

  const renderProductItem = useCallback((item) => {
    const photoUrl = item.photos?.[0]?.photo_url || PLACEHOLDER_IMAGE;
    const isAuctionActive = item.enchere === 1;
    const isActive = item.inActive === false;
    const auctionTimeRemaining = item.temps || 0;
    
    return (
      <div key={`product-${item.id}`} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
        <div className="relative h-48">
          <img 
            src={photoUrl} 
            alt={item.titre}
            className="w-full h-full object-cover bg-gray-100"
            onError={(e) => {
              e.target.src = PLACEHOLDER_IMAGE;
            }}
          />
          
          <div className="absolute top-3 left-3 right-3 flex justify-between">
            {!isActive && (
              <div className="flex items-center bg-green-500 text-white px-3 py-1.5 rounded-full shadow">
                <Icon name="CheckCircle" size={12} color="white" />
                <span className="text-xs font-bold ml-1.5">{t('product.badges.sold')}</span>
              </div>
            )}
            {isAuctionActive && auctionTimeRemaining > 0 && (
              <div className="flex items-center bg-purple-500 text-white px-3 py-1.5 rounded-full shadow">
                <Icon name="Clock" size={12} color="white" />
                <span className="text-xs font-bold ml-1.5">{auctionTimeRemaining}h</span>
              </div>
            )}
          </div>
        </div>

        <div className="p-4">
          <div className="flex justify-between items-start mb-3">
            <div className="flex-1 mr-3">
              <h3 className={`
                font-bold text-lg line-clamp-2
                ${!isActive ? 'text-gray-400 line-through' : 'text-gray-900'}
              `}>
                {item.titre || t('common.noTitle')}
              </h3>
              <div className="mt-2">
                {isActive ? (
                  <div className="flex items-center">
                    <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                    <span className="text-xs font-semibold text-green-600">
                      {t('product.status.active')}
                    </span>
                  </div>
                ) : (
                  <div className="inline-flex items-center bg-gray-400 text-white px-2 py-1 rounded-lg">
                    <Icon name="CheckCircle" size={10} color="white" />
                    <span className="text-xs font-semibold ml-1.5">
                      {t('product.status.sold')}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-500">
                {(item.prix || 0).toLocaleString(t('common.currency') === 'FCFA' ? 'fr-FR' : 'en-US')} {t('common.currency')}
              </div>
              {isAuctionActive && (
                <div className="flex items-center justify-end mt-1 text-purple-600">
                  <Icon name="Gavel" size={12} className="mr-1" />
                  <span className="text-xs font-semibold">{t('product.auction')}</span>
                </div>
              )}
            </div>
          </div>

          <p className="text-gray-600 text-sm line-clamp-2 mb-4">
            {item.description || t('product.noDescription')}
          </p>

          <div className="flex justify-between mb-4 pb-4 border-b border-gray-100">
            <div className="flex items-center text-gray-500">
              <Icon name="MapPin" size={14} className="mr-1.5" />
              <span className="text-sm">{item.city || t('product.unspecified')}</span>
            </div>
            <div className="flex items-center text-gray-500">
              <Icon name="Calendar" size={14} className="mr-1.5" />
              <span className="text-sm">
                {item.createdAt ? new Date(item.createdAt).toLocaleDateString(t('common.currency') === 'FCFA' ? 'fr-FR' : 'en-US') : t('product.unknownDate')}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {item.negociable === 'true' && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg">
                <span className="text-xs font-semibold">{t('product.badges.negotiable')}</span>
              </div>
            )}
            {item.echange === 'true' && (
              <div className="inline-flex items-center bg-purple-500 text-white px-3 py-1.5 rounded-lg">
                <Icon name="RefreshCw" size={10} color="white" className="mr-1.5" />
                <span className="text-xs font-semibold">{t('product.badges.exchange')}</span>
              </div>
            )}
            {item.etat === 'new' || item.etat === 'neuf' ? (
              <div className="inline-flex items-center bg-green-50 text-green-700 px-3 py-1.5 rounded-lg">
                <span className="text-xs font-semibold">{t('product.badges.new')}</span>
              </div>
            ) : item.etat === 'like_new' ? (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg">
                <span className="text-xs font-semibold">{t('product.badges.likeNew')}</span>
              </div>
            ) : null}
          </div>

          {isOwner && (
            <div className="grid grid-cols-2 gap-2">
              <button 
                className="flex items-center justify-center py-2.5 rounded-lg bg-blue-50 border border-blue-100 text-blue-600 hover:bg-blue-100 transition-colors"
                onClick={() => handleView(item)}
              >
                <Icon name="Eye" size={16} className="mr-1.5" />
                <span className="text-xs font-semibold">{t('actions.view')}</span>
              </button>

              {!isActive && !isAuctionActive ? (
                <button 
                  className="flex items-center justify-center py-2.5 rounded-lg bg-purple-50 border border-purple-100 text-purple-600 hover:bg-purple-100 transition-colors"
                  onClick={() => openAuctionModal(item)}
                >
                  <Icon name="Rocket" size={16} className="mr-1.5" />
                  <span className="text-xs font-semibold">{t('actions.auction')}</span>
                </button>
              ) : !isActive && isAuctionActive ? (
                <button 
                  className="flex items-center justify-center py-2.5 rounded-lg bg-red-50 border border-red-100 text-red-600 hover:bg-red-100 transition-colors"
                  onClick={() => handleStopAuction(item.id)}
                >
                  <Icon name="StopCircle" size={16} className="mr-1.5" />
                  <span className="text-xs font-semibold">{t('actions.stop')}</span>
                </button>
              ) : null}

              {isActive && (
                <button 
                  className="flex items-center justify-center py-2.5 rounded-lg bg-green-50 border border-green-100 text-green-600 hover:bg-green-100 transition-colors"
                  onClick={() => handleMarkAsSold(item.id)}
                >
                  <Icon name="CheckCircle" size={16} className="mr-1.5" />
                  <span className="text-xs font-semibold">{t('actions.markSold')}</span>
                </button>
              )}

              <button 
                className="flex items-center justify-center py-2.5 rounded-lg bg-yellow-50 border border-yellow-100 text-yellow-600 hover:bg-yellow-100 transition-colors"
                onClick={() => handleEdit(item.id)}
              >
                <Icon name="Edit" size={16} className="mr-1.5" />
                <span className="text-xs font-semibold">{t('actions.edit')}</span>
              </button>

              <button 
                className="flex items-center justify-center py-2.5 rounded-lg bg-red-50 border border-red-100 text-red-600 hover:bg-red-100 transition-colors"
                onClick={() => handleDelete(item.id)}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                ) : (
                  <>
                    <Icon name="Trash2" size={16} className="mr-1.5" />
                    <span className="text-xs font-semibold">{t('actions.delete')}</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }, [
    handleView, 
    openAuctionModal, 
    handleStopAuction, 
    handleEdit, 
    handleDelete, 
    handleMarkAsSold, 
    isDeleting, 
    isOwner,
    t
  ]);

  const ListFooter = useMemo(() => {
    if (hasMore) {
      return (
        <div className="text-center py-8 mt-4 col-span-full">
          <Button
            variant="outline"
            onClick={onLoadMore}
            loading={isLoading}
            className="min-w-[200px] rounded-xl"
            leftIcon={<Icon name="RefreshCw" size={16} color="#3b82f6" />}
          >
            {t('footer.loadMore')}
          </Button>
          <p className="text-gray-500 text-sm mt-3 font-medium">
            {t('footer.pageInfo', { current: currentPage, total: totalPages })}
          </p>
        </div>
      );
    } else if (filteredProducts.length > 0) {
      return (
        <div className="text-center py-10 mt-4 col-span-full">
          <div className="flex flex-col items-center">
            <div className="flex items-center mb-6">
              <Icon name="CheckCircle" size={24} color="#10b981" />
              <span className="text-gray-600 ml-2 font-medium">
                {searchQuery 
                  ? t('footer.searchResults', { 
                      count: filteredProducts.length,
                      plural: filteredProducts.length > 1 ? 's' : ''
                    })
                  : t('footer.allProducts')
                }
              </span>
            </div>
            {isOwner && (
              <Button
                variant="default"
                leftIcon={<Icon name="Plus" size={16} color="white" />}
                className="min-w-[240px] rounded-xl py-3"
                onClick={() => navigate('/create-listing', {
                  state: {
                    productId: null,
                    isEditMode: false,
                  }
                })}
              >
                {t('footer.addNewProduct')}
              </Button>
            )}
          </div>
        </div>
      );
    }
    return null;
  }, [
    hasMore, 
    isLoading, 
    onLoadMore, 
    currentPage, 
    totalPages, 
    filteredProducts.length, 
    searchQuery, 
    isOwner, 
    navigate,
    t
  ]);

  if (internalLoading && products.length === 0) {
    return (
      <div className="min-h-screen bg-white">
        <ListHeaderComponent
          stats={stats}
          searchQuery={searchQuery}
          handleSearch={handleSearch}
          activeFilter={activeFilter}
          handleFilterChange={handleFilterChange}
          navigate={navigate}
        />
        <div className="container mx-auto px-4 lg:px-6 py-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((_, index) => (
              <ProductSkeleton key={index} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error && products.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center px-10">
          <div className="mb-6">
            <Icon name="AlertCircle" size={64} color="#ef4444" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            {t('errorScreen.title')}
          </h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              variant="default"
              leftIcon={<Icon name="RefreshCw" size={16} color="white" />}
              className="min-w-[140px] rounded-xl"
              onClick={() => fetchProducts()}
            >
              {t('errorScreen.retry')}
            </Button>
            <Button
              variant="outline"
              leftIcon={<Icon name="Home" size={16} color="#64748b" />}
              className="min-w-[140px] rounded-xl"
              onClick={() => navigate(-1)}
            >
              {t('errorScreen.back')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!internalLoading && products.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center px-10">
          <div className="mb-6">
            <Icon name="Package" size={64} color="#e5e7eb" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            {t('emptyScreen.title')}
          </h2>
          <p className="text-gray-600 mb-8">
            {t('emptyScreen.description')}
          </p>
          {isOwner && (
            <Button
              variant="default"
              leftIcon={<Icon name="Plus" size={16} color="white" />}
              className="min-w-[240px] rounded-xl py-3"
              onClick={() => navigate('/create-listing', {
                state: {
                  productId: null,
                  isEditMode: false,
                }
              })}
            >
              {t('emptyScreen.createFirstAd')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!internalLoading && searchQuery && filteredProducts.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center px-10">
          <div className="mb-6">
            <Icon name="SearchX" size={64} color="#e5e7eb" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            {t('emptySearch.title')}
          </h2>
          <p className="text-gray-600 mb-8">
            {t('emptySearch.description', { query: searchQuery })}
          </p>
          <Button
            variant="outline"
            leftIcon={<Icon name="X" size={16} color="#64748b" />}
            className="min-w-[200px] rounded-xl"
            onClick={() => handleSearch('')}
          >
            {t('emptySearch.clear')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" ref={containerRef}>
      <AuctionModal
        visible={auctionModalVisible}
        onClose={closeAuctionModal}
        selectedProduct={selectedProduct}
        auctionTime={auctionTime}
        setAuctionTime={setAuctionTime}
        auctionStartPrice={auctionStartPrice}
        setAuctionStartPrice={setAuctionStartPrice}
        auctionMinBid={auctionMinBid}
        setAuctionMinBid={setAuctionMinBid}
        isSettingAuction={isSettingAuction}
        onStartAuction={handleStartAuction}
        navigate={navigate}
      />
      
      <ListHeaderComponent
        stats={stats}
        searchQuery={searchQuery}
        handleSearch={handleSearch}
        activeFilter={activeFilter}
        handleFilterChange={handleFilterChange}
        navigate={navigate}
      />
      
      <div className="container mx-auto px-4 lg:px-6 py-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map((item) => renderProductItem(item))}
          {ListFooter}
        </div>
      </div>
      
      {/* Style pour cacher la scrollbar */}
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
});

export default Produit;